/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's copyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * reCAPTCHA Enterprise script loader.
 *
 * Provides a single awaitable entry-point that ensures the reCAPTCHA Enterprise
 * script has finished loading before any token-generation attempt.  The resolved
 * promise is memoized so concurrent callers share the same wait.
 */

import { isRecaptchaDisabled } from './recaptcha';
import { isInertSiteKey } from './antibot-bootstrap';
import { resolveAntiBotProviderId } from './recaptcha-config';
import { resetSDKSessionTimestamp } from './recaptcha-session-tracker';

declare global {
  interface Window {
    __RECAPTCHA_LOAD_FAILED__?: boolean;
  }
}

/** Budget when the SDK is already in the document: initialisation only. */
const WARM_TIMEOUT_MS = 8_000;
/** Budget when the tag is being injected now: DNS, TLS and download included. */
const COLD_TIMEOUT_MS = 15_000;
const POLL_INTERVAL_MS = 150;
const LOG_PREFIX = '[ANTIBOT-LOADER]';
const SDK_RELOAD_VERSION = 1;

let _memoized: Promise<boolean> | null = null;

/**
 * Reads the `grecaptcha.enterprise` global, returning it only when both
 * `execute` and `ready` are callable.
 *
 * @returns The Enterprise handle, or `null` when not yet usable.
 */
function getEnterprise(): { ready: (cb: () => void) => void; execute: Function } | null {
  const w = window as any;
  const ent = w.grecaptcha?.enterprise;
  if (ent && typeof ent.execute === 'function' && typeof ent.ready === 'function') {
    return ent;
  }
  return null;
}

/**
 * Rejects the ready-promise if the global load-failed flag was set mid-poll.
 *
 * @param startTime Poll start timestamp (for diagnostics).
 * @param pollCount Current poll iteration count.
 * @param reject Promise reject callback.
 * @returns `true` when it rejected (caller must stop polling).
 */
function rejectIfLoadFailed(
  startTime: number, pollCount: number, reject: (e: Error) => void,
): boolean {
  if (!window.__RECAPTCHA_LOAD_FAILED__) return false;
  console.error(LOG_PREFIX, 'Script load failed mid-poll', {
    elapsed_ms: Date.now() - startTime, poll_count: pollCount, url: window.location.href,
  });
  reject(new AntiBotUnavailableError('reCAPTCHA Enterprise script failed to load'));
  return true;
}

/**
 * Rejects the ready-promise once the deadline passes, logging rich diagnostics
 * about the current `grecaptcha` global state.
 *
 * @param deadline Absolute deadline timestamp.
 * @param timeoutMs Configured timeout (for the message).
 * @param startTime Poll start timestamp (for diagnostics).
 * @param pollCount Current poll iteration count.
 * @param reject Promise reject callback.
 * @returns `true` when it rejected (caller must stop polling).
 */
function rejectIfTimedOut(
  deadline: number, timeoutMs: number, startTime: number, pollCount: number, reject: (e: Error) => void,
): boolean {
  if (Date.now() < deadline) return false;
  const diag = {
    timeout_ms: timeoutMs, elapsed_ms: Date.now() - startTime, poll_count: pollCount,
    grecaptcha_exists: typeof (window as any).grecaptcha !== 'undefined',
    enterprise_exists: typeof (window as any).grecaptcha?.enterprise !== 'undefined',
    execute_type: typeof (window as any).grecaptcha?.enterprise?.execute,
    ready_type: typeof (window as any).grecaptcha?.enterprise?.ready,
    url: window.location.href,
  };
  console.error(LOG_PREFIX, 'Timeout waiting for enterprise.ready()', diag);
  reject(new AntiBotUnavailableError(`reCAPTCHA Enterprise did not become available within ${timeoutMs} ms`));
  return true;
}

/**
 * Polls until the Enterprise SDK is present and its `ready()` fires, or until a
 * load failure/timeout rejects. Drives the {@link createReadyPromise} loop.
 *
 * @param deadline Absolute deadline timestamp.
 * @param timeoutMs Configured timeout (for messages).
 * @param startTime Poll start timestamp (for diagnostics).
 * @param resolve Promise resolve callback (`true` on ready).
 * @param reject Promise reject callback.
 */
function pollForEnterprise(
  deadline: number, timeoutMs: number, startTime: number,
  resolve: (v: boolean) => void, reject: (e: Error) => void,
): void {
  let pollCount = 0;
  const tryReady = () => {
    pollCount++;
    if (rejectIfLoadFailed(startTime, pollCount, reject)) return;
    if (rejectIfTimedOut(deadline, timeoutMs, startTime, pollCount, reject)) return;
    const enterprise = getEnterprise();
    if (enterprise) {
      enterprise.ready(() => {
        console.info(LOG_PREFIX, 'SDK ready', { elapsed_ms: Date.now() - startTime, poll_count: pollCount });
        resolve(true);
      });
    } else {
      setTimeout(tryReady, POLL_INTERVAL_MS);
    }
  };
  tryReady();
}

/**
 * Builds the promise that resolves once the Enterprise SDK is ready, rejecting
 * immediately if the load-failed flag is already set.
 *
 * @param timeoutMs Maximum wait before timing out.
 * @param startTime Poll start timestamp (for diagnostics).
 * @returns A promise resolving `true` when the SDK is ready.
 */
function createReadyPromise(timeoutMs: number, startTime: number): Promise<boolean> {
  return new Promise<boolean>((resolve, reject) => {
    if (window.__RECAPTCHA_LOAD_FAILED__) {
      console.error(LOG_PREFIX, 'Script load failed (__RECAPTCHA_LOAD_FAILED__ = true)', {
        elapsed_ms: Date.now() - startTime, url: window.location.href,
      });
      reject(new AntiBotUnavailableError('reCAPTCHA Enterprise script failed to load'));
      return;
    }
    pollForEnterprise(Date.now() + timeoutMs, timeoutMs, startTime, resolve, reject);
  });
}

/**
 * Awaits full reCAPTCHA Enterprise readiness (via the official `ready()` gate),
 * memoizing the wait so concurrent callers share one promise. Resolves `false`
 * when reCAPTCHA is disabled/mocked.
 *
 * Checking `typeof execute === 'function'` alone is insufficient: the SDK
 * exposes `execute` before internal initialization completes, yielding
 * intermittent BROWSER_ERROR tokens — hence the `ready()`-based gate.
 *
 * @param timeoutMs Maximum time to wait before giving up (default 8s).
 * @returns `true` when ready, `false` when reCAPTCHA is disabled.
 * @throws {AntiBotUnavailableError} When the script failed to load or timed out.
 */
export function awaitRecaptchaReady(timeoutMs?: number): Promise<boolean> {
  if (isRecaptchaDisabled()) return Promise.resolve(false);
  if (_memoized) return _memoized;

  const wasCold = !document.getElementById(SCRIPT_ID);
  ensureRecaptchaScript();
  const budget = timeoutMs ?? (wasCold ? COLD_TIMEOUT_MS : WARM_TIMEOUT_MS);

  _memoized = createReadyPromise(budget, Date.now());
  _memoized.catch(() => { _memoized = null; });
  return _memoized;
}

/**
 * Reset the memoized loader state. Useful after re-injecting the Enterprise
 * script for recovery, or in tests.
 */
export function resetRecaptchaLoader(): void {
  _memoized = null;
}

const SCRIPT_ID = 'recaptcha-enterprise-script';

/**
 * Removes any existing Enterprise script tag and appends a fresh one.
 *
 * @param siteKey The reCAPTCHA site key to render against.
 */
function replaceScriptTag(siteKey: string): void {
  document.getElementById(SCRIPT_ID)?.remove();

  const w = window as any;
  // A fresh tag deserves a clean slate: leaving a previous failure flag set
  // makes the readiness promise reject instantly against a script that is in
  // fact still downloading.
  w.__RECAPTCHA_LOAD_FAILED__ = false;

  const script = document.createElement('script');
  script.id = SCRIPT_ID;
  script.src = `https://www.google.com/recaptcha/enterprise.js?render=${siteKey}`;
  script.async = true;
  script.defer = true;
  script.onerror = () => {
    w.__RECAPTCHA_LOAD_FAILED__ = true;
  };
  try {
    document.head.appendChild(script);
  } catch {
    // Some environments (strict CSP, non-browser DOM) refuse to inject an
    // external script synchronously. Treat it like a pending load and let the
    // readiness poll decide success or timeout, rather than throwing.
  }
  resetSDKSessionTimestamp();
}

/**
 * Injects the reCAPTCHA Enterprise script on demand, at most once per page.
 *
 * Nothing loads the SDK eagerly any more, so this is what puts it in the
 * document -- the first time a token is actually needed. It is a no-op when the
 * Enterprise handle is already usable (whatever put it there) or when a healthy
 * tag is already present, and refuses to inject for an inert site key
 * (empty, an unsubstituted `%PLACEHOLDER%`, or one of the local mock values),
 * which is the guard the old index.html script carried and ReCaptchaProvider
 * never did.
 *
 * The session timestamp is stamped at injection time rather than at module
 * import: with lazy loading the two can be 40 minutes apart, and the stale-session
 * check in recaptcha-token would otherwise reload an SDK that never loaded once.
 *
 * @returns `true` when a usable script tag is present after the call.
 */
export function ensureRecaptchaScript(): boolean {
  if (getEnterprise()) return true;
  if (document.getElementById(SCRIPT_ID) && !window.__RECAPTCHA_LOAD_FAILED__) return true;

  const siteKey = resolveAntiBotProviderId() ?? '';
  if (isInertSiteKey(siteKey)) return false;

  replaceScriptTag(siteKey);
  return true;
}

/**
 * Fully reload the reCAPTCHA Enterprise SDK by removing the existing script,
 * clearing the global grecaptcha object, and re-injecting a fresh script tag.
 *
 * This is necessary when the SDK's internal session with Google servers has expired
 * (after extended page idle time). In this state, `execute()` returns tokens that
 * appear fresh but are rejected by the backend because the underlying session is dead.
 * A full script reload re-establishes the session, similar to a hard page refresh.
 *
 * @param siteKey - The reCAPTCHA site key to use for the new script
 * @param timeoutMs - Maximum time to wait for the new script to load
 */
export async function reloadRecaptchaSDK(siteKey: string, timeoutMs: number = WARM_TIMEOUT_MS): Promise<void> {
  console.warn(LOG_PREFIX, 'Reloading reCAPTCHA SDK to refresh expired session', { siteKey: siteKey.slice(0, 10) });

  _memoized = null;

  const w = window as any;
  if (w.grecaptcha) {
    delete w.grecaptcha;
  }
  w.__RECAPTCHA_LOAD_FAILED__ = false;

  replaceScriptTag(siteKey);

  await awaitRecaptchaReady(timeoutMs);
  console.info(LOG_PREFIX, 'SDK reloaded successfully');
}

/**
 * Typed error thrown when the anti-bot pipeline cannot produce a token.
 */
export class AntiBotUnavailableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AntiBotUnavailableError';
  }
}
